/**
 * Atomato scroll reveals — one shared IntersectionObserver, no dependency.
 *
 * The revealed state is the default state. This script *arms* an element
 * (moves it to its pre-reveal state) and the observer releases it when it
 * scrolls into view. Block the script and nothing is ever hidden: the page
 * renders complete and simply does not animate.
 *
 * Markup contract:
 *   [data-reveal]          fade and rise
 *   [data-reveal-stagger]  fade and rise, direct children in sequence.
 *                          Give it a selector to stagger descendants instead.
 *   [data-reveal-bar]      scales from 0 on the X axis (chart bars)
 *   [data-reveal-draw]     SVG path drawn with stroke-dasharray
 *   [data-count]           counts up to the number already in the DOM,
 *                          from 0 or from [data-count-from]
 *   [data-count-now]       the same count, run once on load instead of on
 *                          entry. For figures that sit ABOVE THE FOLD, where
 *                          the observer deliberately never fires. The rule
 *                          that nothing above the fold animates in is about
 *                          the reveal - the element is present and final at
 *                          first paint here, and only its digits move, so
 *                          LCP and layout are untouched. Reserve the width.
 *
 * The last three are read from inside a [data-reveal] block; they are not
 * observed on their own. Paired CSS lives in assets/atomato.css section 9.
 */

const DURATION = 900; // ms, count-up only — the rest is CSS

/** Counts a number up to the value already rendered in the element. */
function countUp(node) {
  const text = node.textContent.trim();
  const raw = (text.match(/-?[\d.,]+/) || [])[0];
  if (!raw) return;

  const target = parseFloat(raw.replace(/,/g, ''));
  if (!isFinite(target)) return;

  // data-count-from starts the run somewhere other than zero. The targeting
  // panel needs it: the point there is a cart crossing a threshold, and a
  // cart that counts up from $0.00 is not that.
  const fromAttr = parseFloat(node.getAttribute('data-count-from'));
  const from = isFinite(fromAttr) ? fromAttr : 0;

  const decimals = (raw.split('.')[1] || '').length;
  const grouped = raw.includes(',');
  const start = performance.now();

  const frame = (now) => {
    /* Clamped at BOTH ends. rAF hands the callback the timestamp of the
       frame it belongs to, which can predate the performance.now() taken a
       moment earlier in that same frame - so the first tick could run at a
       negative p and render the figure below zero for one frame. Seen as
       "$-1.26" under "$268.60". (31 Aug 2026) */
    const p = Math.min(Math.max((now - start) / DURATION, 0), 1);
    const value = from + (target - from) * (1 - Math.pow(1 - p, 3)); // ease-out cubic
    const fixed = value.toFixed(decimals);
    node.textContent = text.replace(
      raw,
      grouped
        ? Number(fixed).toLocaleString('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        : fixed
    );
    if (p < 1) requestAnimationFrame(frame);
  };

  requestAnimationFrame(frame);
}

/** Reduced motion: arm nothing, so every end state renders immediately. */
if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;

        el.classList.remove('is-armed');
        for (const child of el.querySelectorAll('.is-armed')) child.classList.remove('is-armed');
        for (const n of el.querySelectorAll('[data-count]')) countUp(n);

        observer.unobserve(el);
      }
    },
    { rootMargin: '0px 0px -12% 0px' }
  );

  // Above-the-fold figures opt in explicitly; the observer would skip them.
  for (const n of document.querySelectorAll('[data-count-now]')) countUp(n);

  // Read everything, then write everything. Interleaving them made this the
  // top forced-reflow source on the homepage: each getBoundingClientRect after
  // a classList/style write invalidated layout and forced a fresh one, so the
  // cost scaled with the number of revealed elements rather than staying flat.
  const armable = [];
  for (const el of document.querySelectorAll('[data-reveal], [data-reveal-stagger]')) {
    // Anything already on screen stays put. Nothing above the fold animates
    // in, and arming a visible element would flash it out and back again.
    if (el.getBoundingClientRect().top < innerHeight) continue;
    armable.push(el);
  }

  // getTotalLength is a layout read too, so it belongs in the read pass.
  const draws = [];
  for (const el of armable) {
    for (const path of el.querySelectorAll('[data-reveal-draw]')) {
      draws.push([path, path.getTotalLength()]);
    }
  }

  for (const el of armable) {
    if (el.hasAttribute('data-reveal-stagger')) {
      // A value makes it a selector for descendants, which is what a section
      // needs when wrappers sit between the container and the things that
      // should stagger - Splide puts a track and a list in the way.
      const sel = el.getAttribute('data-reveal-stagger');
      const kids = sel ? el.querySelectorAll(sel) : el.children;
      Array.from(kids).forEach((child, i) => {
        child.classList.add('is-armed');
        child.style.setProperty('--reveal-i', i);
      });
    } else {
      el.classList.add('is-armed');
    }
    observer.observe(el);
  }

  for (const [path, length] of draws) {
    path.style.strokeDasharray = length;
    path.style.setProperty('--draw-len', length);
  }
}
